import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useGuild } from '../contexts/GuildContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
    Ticket, 
    Clock, 
    CheckCircle2, 
    AlertCircle,
    TrendingUp,
    Users,
    ArrowRight,
    BarChart3
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLORS = {
    high: '#ef4444',
    medium: '#f59e0b',
    low: '#22c55e'
};

const CATEGORY_COLORS = ['#5865F2', '#22c55e', '#f59e0b', '#8b5cf6'];

export default function DashboardHome() {
    const { token } = useAuth();
    const { selectedGuild } = useGuild();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [recentTickets, setRecentTickets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!selectedGuild || !token) return;
            
            setLoading(true);
            try {
                const [statsRes, ticketsRes] = await Promise.all([
                    axios.get(`${API}/guilds/${selectedGuild}/stats`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${API}/tickets?guild_id=${selectedGuild}&limit=5`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);
                
                setStats(statsRes.data);
                setRecentTickets(ticketsRes.data.tickets || []);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedGuild, token]);

    const statsCards = [
        {
            title: 'Open Tickets',
            value: stats?.open_tickets || 0,
            icon: Ticket,
            color: 'text-primary',
            bgColor: 'bg-primary/10'
        },
        {
            title: 'Closed Today',
            value: stats?.closed_tickets || 0,
            icon: CheckCircle2,
            color: 'text-green-500',
            bgColor: 'bg-green-500/10'
        },
        {
            title: 'Avg Response',
            value: `${stats?.avg_response_time || 0}m`,
            icon: Clock,
            color: 'text-amber-500',
            bgColor: 'bg-amber-500/10'
        },
        {
            title: 'This Week',
            value: stats?.recent_tickets || 0,
            icon: TrendingUp,
            color: 'text-blue-500',
            bgColor: 'bg-blue-500/10'
        }
    ];

    const priorityData = stats ? [
        { name: 'High', value: stats.priority_breakdown.high, color: PRIORITY_COLORS.high },
        { name: 'Medium', value: stats.priority_breakdown.medium, color: PRIORITY_COLORS.medium },
        { name: 'Low', value: stats.priority_breakdown.low, color: PRIORITY_COLORS.low }
    ] : [];

    const categoryData = stats ? [
        { name: 'Support', value: stats.category_breakdown.support },
        { name: 'Sales', value: stats.category_breakdown.sales },
        { name: 'Bug', value: stats.category_breakdown.bug },
        { name: 'Other', value: stats.category_breakdown.other }
    ] : [];

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-500/20 text-red-500 border-red-500/30';
            case 'medium': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
            case 'low': return 'bg-green-500/20 text-green-500 border-green-500/30';
            default: return '';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return 'bg-green-500/20 text-green-500 border-green-500/30';
            case 'pending': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
            case 'closed': return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
            default: return '';
        }
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="dashboard-loading">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Skeleton className="h-80" />
                    <Skeleton className="h-80" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="dashboard-home">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {statsCards.map((card, index) => (
                    <motion.div
                        key={card.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                    >
                        <Card className="card-interactive" data-testid={`stat-${card.title.toLowerCase().replace(' ', '-')}`}>
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">{card.title}</p>
                                        <p className="text-3xl font-bold font-mono mt-1">{card.value}</p>
                                    </div>
                                    <div className={`p-3 rounded-xl ${card.bgColor}`}>
                                        <card.icon className={`w-6 h-6 ${card.color}`} />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Priority Distribution */}
                <Card data-testid="priority-chart">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-primary" />
                            Priority Distribution
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={priorityData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {priorityData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: 'hsl(var(--card))',
                                            border: '1px solid hsl(var(--border))',
                                            borderRadius: '8px'
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex justify-center gap-6 mt-4">
                            {priorityData.map(item => (
                                <div key={item.name} className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                                    <span className="text-sm text-muted-foreground">{item.name}</span>
                                    <span className="text-sm font-mono font-medium">{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Category Breakdown */}
                <Card data-testid="category-chart">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-primary" />
                            Categories
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={categoryData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis 
                                        dataKey="name" 
                                        stroke="hsl(var(--muted-foreground))"
                                        fontSize={12}
                                    />
                                    <YAxis 
                                        stroke="hsl(var(--muted-foreground))"
                                        fontSize={12}
                                    />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: 'hsl(var(--card))',
                                            border: '1px solid hsl(var(--border))',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="value" 
                                        stroke="#5865F2" 
                                        fill="#5865F2"
                                        fillOpacity={0.2}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Recent Tickets */}
            <Card data-testid="recent-tickets">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Ticket className="w-5 h-5 text-primary" />
                        Recent Tickets
                    </CardTitle>
                    <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate('/dashboard/tickets')}
                        className="flex items-center gap-1"
                        data-testid="view-all-tickets-btn"
                    >
                        View All
                        <ArrowRight className="w-4 h-4" />
                    </Button>
                </CardHeader>
                <CardContent>
                    {recentTickets.length > 0 ? (
                        <div className="space-y-2">
                            {recentTickets.map((ticket, index) => (
                                <motion.div
                                    key={ticket.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className={`ticket-item ${ticket.priority} flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer`}
                                    onClick={() => navigate(`/dashboard/tickets/${ticket.id}`)}
                                    data-testid={`ticket-${ticket.id}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-1 h-10 rounded-full ${ticket.priority === 'high' ? 'bg-red-500' : ticket.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'}`} />
                                        <div>
                                            <p className="font-medium">{ticket.subject}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {ticket.username} • {new Date(ticket.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge variant="outline" className={getPriorityColor(ticket.priority)}>
                                            {ticket.priority}
                                        </Badge>
                                        <Badge variant="outline" className={getStatusColor(ticket.status)}>
                                            {ticket.status}
                                        </Badge>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-muted-foreground">
                            <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>No tickets yet</p>
                            <p className="text-sm">Tickets will appear here when created</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
